import { flushSync } from 'react-dom'
import { closeDialog } from '../app-state/modals/state.dialog'
import { closeDrawer } from '../app-state/modals/state.drawer'
import { hideFloatingPreview } from '../app-state/modals/state.floater'
import { AppRouter } from '../state/state.router'
import { clearHomePageSearchResults } from '../state/state.search'
import { THROTTLE_TIMERS } from '../state/state.timers'
import { closeWhiteboard } from '../state/state.whiteboard'
import { isReducedMotionPreferred } from './browser'

type TransitionTypes = {
    [type in
        | 'NONE'
        | 'PREVIEW'
        | 'SIDEBAR'
        | 'ROW'
        | 'TILE'
        | 'ROOM'
        | 'MENU'
        | 'MODAL'
        | 'FLOATER'
        | 'NAV'
        | 'PINROOM']: TransitionType
}
export type TransitionType = (id?: number) => string

export const TransitionTypes: TransitionTypes = {
    NONE: () => null, // no need for transition (ex: room to room)
    PREVIEW: (id) => `#floating-preview-${id}`, // preview (either room preview or floating preview?)
    SIDEBAR: () => '#sidebar-connections', // an avatar somewher on the page (friends sidebar, rooms or pms listings?)
    ROW: () => '#list-section', // rooms or pms list
    TILE: (id) => `#tile-${id}`, // a grid tile from home, explore, available space, etc
    ROOM: () => '#floating-room', // full room to room preview
    MENU: () => '#menu', //`#menu-${id}`, // model menu
    MODAL: () => '#modal', // any other modal
    FLOATER: (id) => `#floater-${id}`, // floater modal
    NAV: () => '#nav', // nav bar
    PINROOM: (id) => `#pinned-room-${id}`, // pinned rooms
}

const setTransition = (
    id: number,
    elementId: string,
    wipe = false,
    mainVideo = false,
) => {
    const element: HTMLElement = document.querySelector(elementId)
    // console.log('[setTransition]', { id, elementId, wipe, element, mainVideo })
    if (!element) return
    if (mainVideo) setVideoVTN(id, element, wipe)
    else setSnapVideoVTN(id, element, wipe)
    setAvatarVTN(id, element, wipe)
    // setNameVTN(id, element, wipe)
    // setBlurbVTN(id, element, wipe)
}

export const addTransitionNames = (id: number, type: TransitionType) => {
    // console.log('[addTransitionSourceNames]', { id, type })
    switch (type) {
        case TransitionTypes.MENU:
        case TransitionTypes.ROOM:
            return setTransition(id, type(id), false, true)
        case TransitionTypes.NONE:
            return
        default:
            return setTransition(id, type(id))
    }
}
export const removeTransitionNames = (id: number, type: TransitionType) => {
    // console.log('[removeTransitionSourceNames]', { id, type })
    switch (type) {
        case TransitionTypes.MENU:
        case TransitionTypes.ROOM:
            return setTransition(id, type(id), true, true)
        case TransitionTypes.NONE:
            return
        default:
            return setTransition(id, type(id), true)
    }
}

const setVideoVTN = (id: number, _element: HTMLElement, wipe = false) => {
    const video: HTMLElement = document.querySelector(
        `#video-container-${id} div`,
    )
    if (video) {
        // console.log('[setVideoVTN]', { id, wipe, video })
        video.style.viewTransitionName = wipe ? null : `video-${id}` //'video'
    }
}
const setSnapVideoVTN = (id: number, element: HTMLElement, wipe = false) => {
    const video: HTMLElement = element.querySelector(`#snap-video-${id}`)
    if (video) {
        // console.log('[setSnapVideoVTN]', { id, wipe, video })
        video.style.viewTransitionName = wipe ? null : `video-${id}` //'video'
    }
}
const setAvatarVTN = (id: number, element: HTMLElement, wipe = false) => {
    const avatar: HTMLElement = element.querySelector(`#avatar-${id}`)
    if (avatar) {
        // console.log('[setAvatarVTN]', { id, wipe, avatar })
        avatar.style.viewTransitionName = wipe ? null : `avatar-${id}` //'avatar'
    }
}
// const setNameVTN = (id: number, element: HTMLElement, wipe = false) => {
//     const name = element.querySelector(`#name-${id}`) as HTMLElement
//     console.log('[setNameVTN]', { id, wipe, name })
//     if (name) {
//         name.style.viewTransitionName = wipe ? null : 'name'
//     }
// }
// const setBlurbVTN = (id: number, element: HTMLElement, wipe = false) => {
//     const blurb = element.querySelector(`#blurb-${id}`) as HTMLElement
//     console.log('[setBlurbVTN]', { id, wipe, blurb })
//     if (blurb) {
//         blurb.style.viewTransitionName = wipe ? null : 'blurb'
//     }
// }
//
const cleanupModals = (nav: boolean) => {
    if (!nav) return
    closeDrawer(true)
    if (AppRouter.route.visiblePage !== 'search') {
        closeDialog(true)
    }
    closeWhiteboard()
    clearHomePageSearchResults()
}

export const motionTransition = async (
    viewTransition: (transitioning: boolean) => void = () => {},
    options: {
        id?: number
        nav?: boolean
        skipTransition?: boolean
        transitionSource?: TransitionType
        transitionDestination?: TransitionType
    } = {},
) => {
    const {
        id,
        nav = false,
        skipTransition = false,
        transitionSource = TransitionTypes.NONE,
        transitionDestination = TransitionTypes.NONE,
    } = options
    // console.log('[motionTransition]', {
    //     id,
    //     nav,
    //     skipTransition,
    //     isReducedMotionPreferred,
    //     transitionSource,
    //     transitionDestination,
    // })

    // only update the lastTransitionSource if it's something that we could conceivably transition back to
    if (
        transitionSource !== TransitionTypes.FLOATER &&
        transitionSource !== TransitionTypes.MENU &&
        transitionSource !== TransitionTypes.MODAL &&
        transitionSource !== TransitionTypes.PREVIEW &&
        transitionSource !== TransitionTypes.PINROOM
    )
        AppRouter.route.lastTransitionSource = transitionSource

    if (
        typeof document === 'undefined' ||
        !document.startViewTransition ||
        // !id ||
        skipTransition ||
        isReducedMotionPreferred
    ) {
        hideFloatingPreview(transitionDestination === TransitionTypes.ROOM)
        cleanupModals(nav)
        return viewTransition(false)
    }
    if (!id) {
        return document
            .startViewTransition(() => {
                THROTTLE_TIMERS.mainTimerPaused = true
                hideFloatingPreview(
                    transitionDestination === TransitionTypes.ROOM,
                )
                flushSync(() => {
                    viewTransition(true)
                    cleanupModals(nav)
                })
            })
            .finished.then(() => {
                THROTTLE_TIMERS.mainTimerPaused = false
                return true
            })
    }
    // **View Transition basics**
    // 1) startViewTransition takes an 'immediate' screen shot of the page as it exists in the dom
    // - its key that a `view-transition-name` needs to be set on everything that should be transitioned at this point
    // 2) the callback is run (that changes the dom to the new state; this is the opportunity to cleanup
    //    any potential duplicated `view-transition-name` values in the dom that could happen after dom/state changes)
    // 3) after the callback another screenshot will be taken
    // - a matching `view-transition-name` has to exist in this new state, without duplicates, or that
    //   particular transition will be skipped
    // Knowing all this we need to:
    // 1) based on the `TransitionSource` we need to look up the elements in the dom appropriate for transitioning
    // 2) set the `view-transition-name` values on the elements we want
    addTransitionNames(id, transitionSource)
    // 3) remove any potential duplicated `view-transition-name` values we don't care about that could be lingering
    //   (generally just video)
    // 4) call the `startViewTransition`
    // console.log('[motionTransition] beginning transition', {
    //     id,
    //     transitionSource,
    // })
    return document
        .startViewTransition(() => {
            THROTTLE_TIMERS.mainTimerPaused = true
            // 5) in the callback we pass in, we can cleanup the newly set view transitions (since they can still exist
            //    in the dom behind the room, so we should get rid of them to avoid any potential dupes)
            removeTransitionNames(id, transitionSource)
            hideFloatingPreview(transitionDestination === TransitionTypes.ROOM)
            //// TODO: we should look into moving the video manually here so transforms aren't fighting the transitions
            //// transform()
            // 6) change the dom (call our `viewTransition` cb function)
            flushSync(() => {
                viewTransition(true)
                cleanupModals(nav)
            })
            // 7) add the standard transitions to the room elements
            addTransitionNames(id, transitionDestination)
        })
        .finished.then(() => {
            // 8) remove the destination VTNs to reset it back to a clean slate again
            removeTransitionNames(id, transitionDestination)
            THROTTLE_TIMERS.mainTimerPaused = false
            return true
        })
}
